/**
 * Typed client for the mail API.
 *
 * Every request goes through `request()`, so there is one place that attaches
 * the token, one place that turns a non-2xx into an error, and one place that
 * recognises an expired session. Scattering `fetch` through components is how
 * you end up with four different ways of handling a 401.
 *
 * No base URL: the backend serves this bundle from its own origin in
 * production, and Vite proxies /api in development.
 */

export interface ClientConfig {
  readonly mailDomain: string;
  readonly sendingEnabled: boolean;
  readonly retentionDays: number;
}

export interface Inbox {
  readonly address: string;
  readonly createdAt: number;
  readonly messageCount: number;
  readonly unreadCount: number;
  readonly lastMessageAt: number | null;
}

export type MessageDirection = 'in' | 'out';

export interface MessageSummary {
  readonly id: string;
  readonly direction: MessageDirection;
  readonly peerAddress: string;
  readonly peerName: string | null;
  readonly subject: string;
  readonly preview: string;
  readonly createdAt: number;
  readonly seen: boolean;
  readonly hasAttachments: boolean;
}

export interface Attachment {
  readonly id: string;
  readonly filename: string;
  readonly contentType: string;
  readonly sizeBytes: number;
}

export interface MessageDetail extends MessageSummary {
  readonly textBody: string | null;
  readonly htmlBody: string | null;
  readonly attachments: readonly Attachment[];
  /** Echoed back as In-Reply-To so a reply threads in the sender's client. */
  readonly rfcMessageId: string | null;
}

export interface MessagePage {
  readonly items: readonly MessageSummary[];
  readonly nextCursor: string | null;
}

/**
 * A failed request.
 *
 * `unauthorized` is surfaced as a flag rather than left for callers to compare
 * `status === 401`, because every caller reacts the same way and the reaction
 * (drop the token, show the login) is easy to forget.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get unauthorized(): boolean {
    return this.status === 401;
  }
}

interface ErrorBody {
  readonly message?: string | readonly string[];
  readonly errors?: readonly { readonly field: string; readonly message: string }[];
}

/**
 * Pulls something readable out of an error response.
 *
 * Nest returns three different shapes depending on how the error was raised —
 * a plain string, an array of strings, or the validation-pipe object — so
 * unpacking them belongs here rather than in every component that shows one.
 */
async function describeError(response: Response): Promise<string> {
  const body = (await response.json().catch(() => null)) as ErrorBody | null;
  if (!body) return `Request failed (${response.status})`;

  if (body.errors?.length) return body.errors.map((issue) => issue.message).join(' ');
  if (Array.isArray(body.message)) return body.message.join(' ');
  if (typeof body.message === 'string') return body.message;

  return `Request failed (${response.status})`;
}

async function request<T>(
  path: string,
  token: string | null,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (init.body) headers.set('content-type', 'application/json');

  const response = await fetch(`/api/v1${path}`, { ...init, headers });

  if (!response.ok) throw new ApiError(response.status, await describeError(response));

  // 204 on delete and mark-as-read; there is no body to parse.
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

export const api = {
  login: (password: string): Promise<{ token: string; expiresAt: number }> =>
    request('/session', null, { method: 'POST', body: JSON.stringify({ password }) }),

  config: (token: string): Promise<ClientConfig> => request('/config', token),

  listInboxes: (token: string): Promise<Inbox[]> => request('/inboxes', token),

  createInbox: (token: string, name: string): Promise<Inbox> =>
    request('/inboxes', token, { method: 'POST', body: JSON.stringify({ name }) }),

  deleteInbox: (token: string, address: string): Promise<void> =>
    request(`/inboxes/${encodeURIComponent(address)}`, token, { method: 'DELETE' }),

  listMessages: (token: string, address: string, cursor?: string): Promise<MessagePage> => {
    const query = new URLSearchParams({ limit: '50' });
    if (cursor) query.set('cursor', cursor);

    return request(`/inboxes/${encodeURIComponent(address)}/messages?${query}`, token);
  },

  getMessage: (token: string, id: string): Promise<MessageDetail> => request(`/messages/${id}`, token),

  markSeen: (token: string, id: string): Promise<void> =>
    request(`/messages/${id}/seen`, token, { method: 'POST' }),

  deleteMessage: (token: string, id: string): Promise<void> =>
    request(`/messages/${id}`, token, { method: 'DELETE' }),

  send: (
    token: string,
    message: { from: string; to: string; subject: string; text: string; inReplyTo?: string },
  ): Promise<MessageDetail> =>
    request('/messages', token, { method: 'POST', body: JSON.stringify(message) }),

  /**
   * Downloads an attachment and hands it to the browser as a file.
   *
   * A plain `<a href download>` cannot carry the Authorization header, and the
   * alternative — accepting the token as a query parameter — would put it in
   * the server's access logs and in the browser's history. So the bytes are
   * fetched, wrapped in an object URL, and clicked programmatically.
   */
  downloadAttachment: async (
    token: string,
    messageId: string,
    attachment: Attachment,
  ): Promise<void> => {
    const response = await fetch(`/api/v1/messages/${messageId}/attachments/${attachment.id}`, {
      headers: { authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new ApiError(response.status, await describeError(response));

    const url = URL.createObjectURL(await response.blob());

    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      link.click();
    } finally {
      // Revoking immediately is safe: the click has already handed the blob to
      // the download manager. Leaving it would pin the bytes in memory for the
      // lifetime of the page.
      URL.revokeObjectURL(url);
    }
  },
};
